import { t, onLang } from './i18n.js';

/**
 * The centre-screen announcement: DOUBLE KILL, VICTORY, BOMB PLANTED AT A.
 *
 * ## This event had no listener at all
 *
 * `ui:banner` has been emitted since the scorestreak work and was never consumed.
 * Six emitters, zero subscribers — measured on the running game, not inferred.
 * So `RAMPAGE`, `MISSION COMPLETE`, and every round result the new modes announce
 * were being published into nothing, and a Search & Destroy round could be won or
 * lost without a single word on screen.
 *
 * It is the kind of bug that survives because nothing fails: the emitter works, the
 * bus works, and no error is ever raised for an event nobody wants.
 *
 * ## Words, not sentences
 *
 * Emitters send an i18n `key` and any values it needs, never a finished string.
 * Word order differs between the languages — `A CAPTURED` against `A を確保` — so a
 * line assembled in code reads as a translation in whichever language lost. The
 * whole sentence lives in the table and the zone letter drops into it.
 *
 * `text` is still accepted for anything not yet converted, so no caller breaks.
 */

export const TUNING = {
  /** Seconds a banner stays up. Long enough to read mid-firefight, short enough to ignore. */
  life: 2.0,
  /** A second banner inside this window replaces the first rather than queueing. */
  replaceWindow: 0.35,
  maxQueue: 3,
};

export class Banner {
  constructor(ctx) {
    this.ctx = ctx;
    this.queue = [];
    this.current = null;
    this.timer = 0;

    const host = document.getElementById('hud') ?? document.body;
    const el = document.createElement('div');
    el.className = 'bn-root';
    el.innerHTML = '<div class="bn-text"></div>';
    host.appendChild(el);
    this.root = el;
    this.textEl = el.querySelector('.bn-text');

    this._off = ctx.events.on('ui:banner', (p) => this.push(p));
    this._offLang = onLang(() => {
      // Re-translate whatever is on screen, so a language change mid-match does not
      // leave one word in the old language until it expires.
      if (this.current) this.textEl.textContent = this._render(this.current);
    });

    // 'ui', the one phase that keeps running while paused — a banner that freezes
    // behind the pause menu is still on screen when the player comes back.
    ctx.engine.registerUpdate((dt) => this.update(dt), 'ui');
  }

  _render(p) {
    return p.key ? t(p.key, p.params ?? null) : (p.text ?? '');
  }

  push(p) {
    if (!p) return;
    const text = this._render(p);
    if (!text) return;
    const item = { ...p, text };

    // A kill banner arriving on the heels of a round result must not bury it, and
    // two multikill callouts in the same second are one event, not two.
    if (this.current && this.timer > TUNING.life - TUNING.replaceWindow) {
      this.current = item;
      this._paint();
      return;
    }
    if (this.queue.length >= TUNING.maxQueue) this.queue.shift();
    this.queue.push(item);
    if (!this.current) this._next();
  }

  _next() {
    this.current = this.queue.shift() ?? null;
    this.timer = this.current ? TUNING.life : 0;
    this._paint();
  }

  _paint() {
    if (!this.current) {
      this.root.classList.remove('is-on');
      return;
    }
    this.textEl.textContent = this.current.text;
    this.root.dataset.tone = this.current.tone ?? 'info';
    // Restart the entry animation even when a banner replaces one already showing.
    this.root.classList.remove('is-on');
    void this.root.offsetWidth;
    this.root.classList.add('is-on');
  }

  update(dt) {
    if (!this.current) {
      if (this.queue.length) this._next();
      return;
    }
    this.timer -= dt;
    if (this.timer <= 0) this._next();
  }

  dispose() {
    this._off?.();
    this._offLang?.();
    this.root.remove();
  }
}
